import { useEffect, useRef, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { Video, MapPin, MessageSquare, Link2, Copy, Share2, XCircle, Phone, Loader2, ImageIcon, ShieldCheck, Radio } from "lucide-react";

const MM_LABELS = { live_video: "Live video", location: "Updated location", photo: "Photograph upload",
  video: "Video upload", silent_chat: "Silent chat", accessibility: "Accessibility communication" };

function MediaThumb({ m }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    client.get(`/media-sessions/media/${m.path}`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [m.path]);
  if (!src) return <div className="w-24 h-20 border border-[#2C2D35] rounded-sm flex items-center justify-center"><Loader2 size={12} className="animate-spin text-[#9CA3AF]" /></div>;
  return m.kind === "video"
    ? <video src={src} controls className="w-44 h-28 rounded-sm border border-[#2C2D35]" data-testid="mm-video" />
    : <img src={src} alt={m.filename} className="w-24 h-20 object-cover rounded-sm border border-[#2C2D35]" data-testid="mm-photo" />;
}

export default function MultimediaPanel({ incident }) {
  const [sessions, setSessions] = useState([]);
  const [req, setReq] = useState({ live_video: true, location: true, photo: true, video: false, silent_chat: true, accessibility: false });
  const [reason, setReason] = useState("");
  const [chatText, setChatText] = useState({});
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const load = () => client.get(`/incidents/${incident.id}/media-sessions`).then((r) => setSessions(r.data.sessions || [])).catch(() => {});
  useEffect(() => {
    load();
    timer.current = setInterval(load, 4000);
    return () => clearInterval(timer.current);
  }, [incident.id]); // eslint-disable-line

  const create = async () => {
    const requested = Object.keys(req).filter((k) => req[k]);
    if (!requested.length) { toast.error("Select at least one item to request"); return; }
    if (!reason.trim()) { toast.error("An operational reason is required"); return; }
    setBusy(true);
    try {
      const r = await client.post(`/incidents/${incident.id}/media-sessions`, { requested, reason: reason.trim() });
      const url = `${window.location.origin}/caller/${r.data.session.token}`;
      try { await navigator.clipboard.writeText(url); toast.success("Secure caller link created & copied"); }
      catch { toast.success("Secure caller link created"); }
      setReason(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create link"); }
    setBusy(false);
  };
  const act = async (fn, msg) => { try { await fn(); if (msg) toast.success(msg); load(); } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); } };
  const quality = (s) => {
    if (s.ended) return ["ENDED", "#6B7280"];
    if (!s.last_seen) return ["AWAITING CALLER", "#F59E0B"];
    const age = (Date.now() - new Date(s.last_seen).getTime()) / 1000;
    return age < 15 ? ["CONNECTED", "#10B981"] : age < 60 ? ["WEAK", "#F59E0B"] : ["DISCONNECTED", "#EF4444"];
  };

  return (
    <div className="border border-[#2C2D35] bg-[#141518] rounded-sm p-3" data-testid="multimedia-panel">
      <div className="font-head text-[11px] font-bold tracking-wider text-[#22D3EE] mb-2 flex items-center gap-1.5">
        <Radio size={12} /> MULTIMEDIA COMMUNICATIONS
      </div>
      {/* Send secure caller link */}
      <div className="border border-[#2C2D35] rounded-sm p-2.5 mb-3">
        <div className="font-mono text-[10px] text-[#9CA3AF] uppercase mb-1.5">Send secure caller link — request:</div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mb-2">
          {Object.entries(MM_LABELS).map(([k, label]) => (
            <label key={k} className="flex items-center gap-1.5 font-mono text-[11px] text-[#C9CDD4] cursor-pointer">
              <input type="checkbox" data-testid={`mm-req-${k}`} checked={!!req[k]} onChange={(e) => setReq({ ...req, [k]: e.target.checked })} /> {label}
            </label>
          ))}
        </div>
        <div className="flex gap-2">
          <input data-testid="mm-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Operational reason (required, audited)"
            className="flex-1 bg-[#0d0e11] border border-[#2C2D35] px-2 py-1.5 text-[11px] font-mono text-white rounded-sm outline-none focus:border-[#22D3EE]" />
          <button data-testid="mm-create-link" onClick={create} disabled={busy}
            className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-head font-bold bg-[#22D3EE] text-black rounded-sm disabled:opacity-50">
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Link2 size={11} />} CREATE LINK
          </button>
        </div>
        <div className="mt-1.5 font-mono text-[9px] text-[#6B7280]">
          Caller camera/microphone can NEVER be activated without the caller's affirmative consent. Telephony callback/SMS: <span className="text-[#F59E0B]">DEMONSTRATION MODE</span> until configured in the Integration Center.
        </div>
      </div>
      {/* Sessions */}
      {sessions.map((s) => {
        const [ql, qc] = quality(s);
        const lastLoc = (s.locations || [])[s.locations?.length - 1];
        return (
          <div key={s.id} className="border border-[#2C2D35] rounded-sm p-2.5 mb-2" data-testid={`mm-session-${s.id}`}>
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded border" style={{ color: qc, borderColor: qc }}>{ql}</span>
              <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded border" data-testid="mm-consent-status"
                style={{ color: s.consent?.granted ? "#10B981" : s.consent ? "#EF4444" : "#F59E0B", borderColor: s.consent?.granted ? "#10B981" : s.consent ? "#EF4444" : "#F59E0B" }}>
                <ShieldCheck size={9} className="inline mr-0.5" />{s.consent ? (s.consent.granted ? "CONSENT GRANTED" : "CONSENT DECLINED") : "CONSENT PENDING"}
              </span>
              {s.shared_with_responders && <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded border text-[#A855F7] border-[#A855F7]">SHARED WITH RESPONDERS</span>}
              <span className="font-mono text-[9px] text-[#6B7280]">by {s.requested_by} · {s.ts?.slice(0, 16)}</span>
              <span className="ml-auto flex gap-1.5">
                <button data-testid="mm-copy-link" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/caller/${s.token}`); toast.success("Link copied"); }}
                  className="px-2 py-1 text-[9px] font-head font-bold border border-[#22D3EE] text-[#22D3EE] rounded-sm"><Copy size={9} className="inline mr-0.5" />LINK</button>
                <a href={`tel:${incident.caller?.phone || ""}`} className="px-2 py-1 text-[9px] font-head font-bold border border-[#10B981] text-[#10B981] rounded-sm"><Phone size={9} className="inline mr-0.5" />CALLBACK</a>
                {!s.ended && <>
                  <button data-testid="mm-share" onClick={() => act(() => client.post(`/media-sessions/${s.id}/share`, {}), "Shared with responders")}
                    className="px-2 py-1 text-[9px] font-head font-bold border border-[#A855F7] text-[#A855F7] rounded-sm"><Share2 size={9} className="inline mr-0.5" />SHARE</button>
                  <button data-testid="mm-end" onClick={() => act(() => client.post(`/media-sessions/${s.id}/end`), "Session ended")}
                    className="px-2 py-1 text-[9px] font-head font-bold border border-[#EF4444] text-[#EF4444] rounded-sm"><XCircle size={9} className="inline mr-0.5" />END</button>
                </>}
              </span>
            </div>
            <div className="grid md:grid-cols-2 gap-2">
              <div>
                {s.video_active && s.live_frame && (
                  <div className="mb-2">
                    <div className="font-mono text-[9px] text-[#EF4444] font-bold mb-0.5 animate-pulse">● LIVE CALLER VIDEO (voluntary — caller may stop at any time)</div>
                    <img src={s.live_frame.image} alt="Live" className="w-full max-w-[280px] rounded-sm border border-[#EF4444]" data-testid="mm-live-frame" />
                  </div>
                )}
                {lastLoc && (
                  <div className="font-mono text-[10px] text-[#C9CDD4] mb-1" data-testid="mm-location">
                    <MapPin size={10} className="inline text-[#22D3EE] mr-1" />
                    {lastLoc.lat.toFixed(5)}, {lastLoc.lng.toFixed(5)} ±{Math.round(lastLoc.accuracy)}m · {lastLoc.ts?.slice(11, 19)}
                    <span className="text-[#6B7280]"> · {s.locations.length} breadcrumb(s)</span>
                  </div>
                )}
                {(s.media || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-1">
                    <ImageIcon size={11} className="text-[#9CA3AF] mt-1" />
                    {s.media.map((m, i) => <MediaThumb key={i} m={m} />)}
                  </div>
                )}
              </div>
              <div>
                <div className="border border-[#2C2D35] rounded-sm bg-[#0d0e11] h-32 overflow-y-auto p-1.5 mb-1" data-testid="mm-chat">
                  {(s.chat || []).map((m, i) => (
                    <div key={i} className={`font-mono text-[10px] py-0.5 ${m.from === "caller" ? "text-[#22D3EE]" : "text-[#C9CDD4]"}`}>
                      <span className="text-[#6B7280]">{m.ts?.slice(11, 16)}</span> <b>{m.from === "caller" ? "CALLER" : m.name}:</b> {m.text}
                    </div>
                  ))}
                  {!(s.chat || []).length && <div className="font-mono text-[9px] text-[#6B7280]">Silent chat — messages appear here.</div>}
                </div>
                {!s.ended && (
                  <div className="flex gap-1">
                    <input data-testid="mm-chat-input" value={chatText[s.id] || ""} onChange={(e) => setChatText({ ...chatText, [s.id]: e.target.value })}
                      onKeyDown={(e) => { const v = (chatText[s.id] || "").trim(); if (e.key === "Enter" && v) { act(() => client.post(`/media-sessions/${s.id}/chat`, { text: v })); setChatText({ ...chatText, [s.id]: "" }); } }}
                      placeholder="Message the caller…" className="flex-1 bg-[#0d0e11] border border-[#2C2D35] px-2 py-1 text-[10px] font-mono text-white rounded-sm outline-none" />
                    <button data-testid="mm-chat-send" onClick={() => { const v = (chatText[s.id] || "").trim(); if (v) { act(() => client.post(`/media-sessions/${s.id}/chat`, { text: v })); setChatText({ ...chatText, [s.id]: "" }); } }}
                      className="px-2 py-1 text-[9px] font-head font-bold bg-[#22D3EE] text-black rounded-sm"><MessageSquare size={10} /></button>
                  </div>
                )}
              </div>
            </div>
            <details className="mt-1.5">
              <summary className="font-mono text-[9px] text-[#6B7280] cursor-pointer">COMMUNICATION TIMELINE ({(s.timeline || []).length}) — full audit</summary>
              <div className="max-h-28 overflow-y-auto mt-1">
                {(s.timeline || []).slice().reverse().map((t, i) => (
                  <div key={i} className="font-mono text-[9px] text-[#9CA3AF] py-px">{t.ts?.slice(0, 19)} · <b>{t.actor}</b> · {t.action.toUpperCase()} {t.detail && `— ${t.detail}`}</div>
                ))}
              </div>
            </details>
          </div>
        );
      })}
      {!sessions.length && <div className="font-mono text-[10px] text-[#6B7280]">No caller media sessions yet — create a secure link above.</div>}
    </div>
  );
}
